import { inject, Injectable } from '@angular/core';
import {
    BehaviorSubject,
    filter,
    finalize,
    map,
    Observable,
    of,
    switchMap,
    take,
    tap,
} from 'rxjs';
import {
    SlotPriorityUpdate,
    ZonePriorityUpdate,
} from '@control-tower/platform-wms';
import { WarehouseStructure } from '../../entities/warehouse.dto';
import { WarehouseDataService } from '../warehouses/warehouse.data.service';
import { ZoneFacade } from '../../application/zone.facade';
import { ZoneDto } from '../../entities/zone.dto';
import { LoggerService } from '@pf/shared-services';

@Injectable()
export class WarehouseStructureService {
    private readonly warehouseDataService = inject(WarehouseDataService);
    private readonly zoneFacade = inject(ZoneFacade);
    private readonly logger = inject(LoggerService);

    private readonly _structure$ =
        new BehaviorSubject<WarehouseStructure | null>(null);
    structure$ = this._structure$.asObservable();

    private readonly _loading$ = new BehaviorSubject<boolean>(false);
    loading$ = this._loading$.asObservable();

    loadWarehouseStructure(id: string): Observable<WarehouseStructure> {
        if (this._loading$.value) {
            return this._structure$.pipe(
                filter((s): s is WarehouseStructure => s !== null),
                take(1)
            );
        }

        this._loading$.next(true);
        this.zoneFacade.resetStoreForWarehouse(id);
        return this.getStructure$(id).pipe(
            tap(structure => {
                this._structure$.next(structure);
            }),
            finalize(() => {
                this._loading$.next(false);
            })
        );
    }

    updateStructure$(
        id: string,
        zones: ZonePriorityUpdate[],
        slots: SlotPriorityUpdate[]
    ) {
        return this.warehouseDataService
            .updateStructure$(id, zones, slots)
            .pipe(
                tap(structure => {
                    this.zoneFacade.resetStoreForWarehouse(id);
                    this.zoneFacade.setNodes(structure);
                    this._structure$.next(structure);
                })
            );
    }

    updateZonePriorities$(
        warehouseId: string,
        zones: ZoneDto[]
    ): Observable<WarehouseStructure | null> {
        if (zones.length === 0) {
            return of(null);
        }

        let nextZonePriority = zones[0].zonePriority || 1;
        const updatedZones = zones.reduce<ZonePriorityUpdate[]>((acc, zone) => {
            if (!zone.zonePriority || zone.zonePriority < nextZonePriority) {
                acc.push({
                    id: zone.id,
                    priority: nextZonePriority,
                });
                nextZonePriority++;
            } else {
                nextZonePriority = zone.zonePriority + 1;
            }
            return acc;
        }, []);

        return updatedZones.length > 0
            ? this.updateStructure$(warehouseId, updatedZones, [])
            : of(null);
    }

    reorderZonePriorities(
        zoneIdToMove: string,
        zoneIdToMoveBefore: string,
        moveAfter = false
    ): Observable<WarehouseStructure | null> {
        const zoneToMove = this.zoneFacade.getById(zoneIdToMove);
        const zoneToMoveBefore = this.zoneFacade.getById(zoneIdToMoveBefore);

        if (!zoneToMove || !zoneToMoveBefore) {
            this.logger.error('Invalid move. Zone information is not set!');
            return of(null);
        }

        if (zoneToMove.warehouseId !== zoneToMoveBefore.warehouseId) {
            this.logger.error(
                'Invalid move. Zones are not in the same warehouse!'
            );
            return of(null);
        }

        const zoneToMoveType = this.zoneFacade.getZoneTypeOfZone(zoneToMove);
        if (!zoneToMoveType) {
            this.logger.error(
                'Invalid move. Zone type information is misconfigured!'
            );
            return of(null);
        }

        const zonesToMoveUpInPriority =
            this.zoneFacade.getZonesWithMatchingLocationIdentifiers(
                zoneToMove.warehouseId,
                zoneToMove.locationIdentifiers
            );
        const zonesToMoveBefore =
            this.zoneFacade.getZonesWithMatchingLocationIdentifiers(
                zoneToMoveBefore.warehouseId,
                zoneToMoveBefore.locationIdentifiers
            );

        const allZones = this.zoneFacade.getZonesByPriority(
            zoneToMove.warehouseId
        );

        if (
            !zonesToMoveUpInPriority.length ||
            !zonesToMoveBefore.length ||
            !allZones.length
        ) {
            this.logger.error('Invalid move. Cannot find zones to move.');
            return of(null);
        }

        const updatedZones = this.reorderZones(
            allZones,
            zonesToMoveBefore,
            zonesToMoveUpInPriority,
            moveAfter
        );

        if (allZones.length !== updatedZones.length) {
            this.logger.error('Invalid move. Ordering logic is incorrect.');
            return of(null);
        }

        return this.updateZonePriorities$(zoneToMove.warehouseId, updatedZones);
    }

    private getStructure$(id: string): Observable<WarehouseStructure> {
        return this.warehouseDataService.getStructure$(id).pipe(
            tap(structure => {
                this.zoneFacade.setNodes(structure);
            }),
            switchMap(structure => {
                return this.ensureZonePrioritiesAreSet$(id, structure);
            })
        );
    }

    private reorderZones(
        allZones: ZoneDto[],
        zonesToMoveBefore: ZoneDto[],
        zonesToMoveUpInPriority: ZoneDto[],
        moveAfter = false
    ) {
        // Create new list of all zones with the zones reordered. Then we pass that list to the warehouse facade updateZonePriorities$
        const updatedZones = [...allZones];
        const moveIds = new Set(zonesToMoveUpInPriority.map(z => z.id));
        const indexOfZoneToMove = allZones.findIndex(z => moveIds.has(z.id));
        const indexOfZoneToMoveBefore = allZones.findIndex(
            z => z.id === zonesToMoveBefore[0].id
        );

        // Remove the zone(s) being moved from the list.
        updatedZones.splice(indexOfZoneToMove, zonesToMoveUpInPriority.length);

        // The target subtree (zonesToMoveBefore) is contiguous in the priority
        // list. When inserting relative to it, account for the fact that
        // removing the moved zone(s) shifts indices left when the moved zone
        // was positioned before the target. Failing to account for this is the
        // original off-by-one bug that placed moved-down zones one position too
        // far down.
        const movedBeforeTarget = indexOfZoneToMove < indexOfZoneToMoveBefore;
        const shift = movedBeforeTarget ? zonesToMoveUpInPriority.length : 0;

        let insertIndex: number;
        if (moveAfter) {
            // Insert immediately after the target's subtree. The target subtree
            // ends at indexOfZoneToMoveBefore + zonesToMoveBefore.length - 1.
            const lastTargetIndex =
                indexOfZoneToMoveBefore + zonesToMoveBefore.length - 1;
            insertIndex = lastTargetIndex - shift + 1;
        } else {
            insertIndex = indexOfZoneToMoveBefore - shift;
        }

        updatedZones.splice(insertIndex, 0, ...zonesToMoveUpInPriority);
        return updatedZones;
    }

    private ensureZonePrioritiesAreSet$(
        warehouseId: string,
        structure: WarehouseStructure
    ): Observable<WarehouseStructure> {
        const zones = this.zoneFacade.getZonesByPriority(warehouseId);

        return this.updateZonePriorities$(warehouseId, zones).pipe(
            tap(updatedStructure => {
                this.zoneFacade.setNodes(updatedStructure);
            }),
            map(updatedStructure => updatedStructure || structure)
        );
    }
}
